import React, { useEffect, useState, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Trans, useTranslation } from "react-i18next";
import {
  FormInput,
  FormSwitch,
  FormWrapper,
  ManualLoader,
  Messages,
} from "../../../components";
import { WizardShell } from "../../../components/Layouts/WizardShell";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { commonSpacing, theme } from "../../../shared/theme/theme";
import { updateBindingsChange } from "../../../shared/utilities/formUtilities";
import {
  useCreateProspectUserMutation,
  useGetPropertiesMutation,
  useGetUnitDataMutation,
  useLazyGetLeasePersonQuery,
  useGetValidateUnitDataMutation,
} from "../../../store/services/publicApi";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { useAuth0 } from "@auth0/auth0-react";
import { RegisterReviewContainer } from "./Register.style";
import { ConfirmUnitInformation } from "./ConfirmUnitInformation";
import {
  buildMissingLeaseTermAvailabilityPath,
  getAffordableUnitAssignmentMode,
  getLeaseTermSearchParam,
  hasLeaseTermParam,
  isAffordableApplicationFlow,
  shouldRedirectDirectAffordableToAvailability,
  shouldShowMoveInOutOfWindowRecovery,
} from "./unitHelpers";
import { useGetApplicationsMutation } from "src/store/services/privateApi";
import Footer from "./Footer";
import { FullStory } from "@fullstory/browser";
import {
  identifyFullStoryUser,
  safeLogout,
} from "src/shared/utilities/fullStorySession";
import { isInviteAuthMismatch } from "./inviteAuth";

type RegistrationPropertyDetails = {
  id?: string;
  name: string;
  city: string;
  affordableHousing?: boolean;
  affordableUnitAssignmentMode?: "holding" | "direct";
  abbreviation?: string;
};

export const RegisterProspectScreen = () => {
  const { t, i18n } = useTranslation();
  const { loginWithRedirect, isAuthenticated, user, logout } = useAuth0();
  const [isconfirmCriteria, setconfirmCriteria] = useState(false);
  const [isconfirmPrivacy, setconfirmPrivacy] = useState(false);
  const [isconfirmComms, setconfirmComms] = useState(false);
  const [unitData, setUnitData] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const formContainerRef = useRef<HTMLDivElement>(null);

  // Hide Zoho help widget on this page
  useEffect(() => {
    setTimeout(() => {
      // @ts-ignore
      window["ZohoHCAsap"]?.Action?.("hideLauncher");
    }, 2000);
  }, []);

  const [
    getPropertiesForIDMutation,
    { isLoading: isLoadingGetPropertiesForID },
  ] = useGetPropertiesMutation();

  const [
    getUnitDataMutation,
    { isLoading: isLoadingGetUnitData, error: unitDataError },
  ] = useGetUnitDataMutation();

  const [
    getValidateUnitDataMutation,
    { isLoading: isLoadingValidateUnitData, error: validateUnitDataError },
  ] = useGetValidateUnitDataMutation();

  const [getApplications] = useGetApplicationsMutation();

  const [propertyID, setPropertyID] = useState<string | null>(null);
  const [mutation, { isLoading: isLoadingCreateProspect }] =
    useCreateProspectUserMutation();
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [searchParams, setSearchParams] = useSearchParams();

  const [unitReviewed, setUnitReviewed] = useState<boolean>(
    searchParams.get("unitReviewed") === "true"
  );

  const [selectedProspectSource, setSelectedProspectSource] =
    useState<string>("");

  // Scroll to top when toggling into the reviewed view so mobile users start at the form header
  useEffect(() => {
    if (unitReviewed) {
      // Use requestAnimationFrame to ensure DOM is fully updated before scrolling
      requestAnimationFrame(() => {
        if (formContainerRef.current) {
          formContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    }
  }, [unitReviewed]);

  /**
   * define the validation schema for form validation
   */
  const registerFormSchema = yup
    .object({
      firstName: yup.string().required(t("account.registerProspect.required")),
      middleName: yup.string().optional(),
      lastName: yup.string().required(t("account.registerProspect.required")),
      email: yup
        .string()
        .email(t("account.registerProspect.emailInvalid"))
        .required(t("account.registerProspect.required")),
      language: yup.string().required(t("account.registerProspect.required")),
      prospectSource: yup.string().optional(),
      referrerName: yup.string().optional(),
      leasingAgent: yup.string().optional(),
      password: yup.string().when("$isAuthenticated", {
        is: false,
        then: (s) =>
          s
            .min(8, t("account.registerProspect.passwordMinLength"))
            .matches(/[A-Z]/, t("account.registerProspect.passwordUppercase"))
            .matches(/[a-z]/, t("account.registerProspect.passwordLowercase"))
            .matches(/[0-9]/, t("account.registerProspect.passwordNumber"))
            .required(t("account.registerProspect.required")),
        otherwise: (s) => s.notRequired(),
      }),
      // .required(t("account.registerProspect.required")),
      confirmPassword: yup.string().when("$isAuthenticated", {
        is: false,
        then: (s) =>
          s
            .required(t("account.registerProspect.required"))
            .oneOf(
              [yup.ref("password"), null],
              t("account.registerProspect.passwordsMustMatch")
            ),
        otherwise: (s) => s.notRequired(),
      }),
    })
    .required();
  const getApplicationFormPath = (
    propertyForRoute: RegistrationPropertyDetails = propertyDetails
  ) => {
    return isAffordableApplicationFlow(unitData, propertyForRoute)
      ? "/application/affordable/form"
      : "/application/form";
  };

  const resolvePropertyDetailsForRouting =
    async (): Promise<RegistrationPropertyDetails> => {
      if (hasFullPropertyDetails || !propertyID) {
        return propertyDetails;
      }

      const res = (await getPropertiesForIDMutation(propertyID)) as {
        data?: RegistrationPropertyDetails;
      };
      if (res.data) {
        setPropertyDetails(res.data);
        return res.data;
      }

      return propertyDetails;
    };

  const webAuthnSignIn = async (
    afterRegistration = false,
    applicationId = ""
  ) => {
    const routePropertyDetails = await resolvePropertyDetailsForRouting();
    const formPath = getApplicationFormPath(routePropertyDetails);
    await loginWithRedirect({
      authorizationParams: {
        responseType: "code",
        redirectUri: `${window.location.origin}${
          isAuthenticated || afterRegistration
            ? formPath
            : "/application/register"
        }${
          window.location.search
        }&unitReviewed=${unitReviewed}&applicationId=${applicationId}`,
        mode: "login",
        login_hint: getValues("email"),
      },
      appState: {
        returnTo: `${
          isAuthenticated || afterRegistration
            ? formPath
            : "/application/register"
        }${
          window.location.search
        }&unitReviewed=${unitReviewed}&applicationId=${applicationId}`,
      },
    });
  };
  /**
   * initialize the hook form
   */
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    control,
    trigger,
    formState: { errors },
  } = useForm<{
    firstName: string;
    middleName?: string;
    lastName: string;
    email: string;
    password?: string;
    confirmPassword?: string;
    language?: string;
    prospectSource?: string;
    referrerName?: string;
    leasingAgent?: string;
  }>({
    resolver: yupResolver(registerFormSchema),
    context: { isAuthenticated },
  });

  // Ensure language defaults to English on first render
  useEffect(() => {
    if (!getValues("language")) {
      setValue("language", "en", { shouldValidate: false });
      i18n.changeLanguage("en");
    }
  }, []);

  const [isPropertyAndUnitValidated, setIsPropertyAndUnitValidated] =
    useState(false);

  const [propertyDetails, setPropertyDetails] =
    useState<RegistrationPropertyDetails>({
      name: "",
      city: "",
    });

  const personId = searchParams.get("personId");
  let unitID = searchParams.get("unitID");
  let unitNumber = searchParams.get("unitNumber");
  let moveInDate = searchParams.get("moveInDate");
  let term = getLeaseTermSearchParam(searchParams);
  let leaseId = searchParams.get("leaseId");
  let pricing = searchParams.get("pricing");
  let applicationId = searchParams.get("applicationId");
  const hasSelectedLeaseTerm = hasLeaseTermParam(term);
  const hasFullPropertyDetails =
    propertyDetails.affordableHousing !== undefined ||
    Boolean(propertyDetails.name);
  const needsUnitQuote = Boolean((unitNumber || unitID) && !leaseId);
  const isRegisterContextLoading =
    !isPropertyAndUnitValidated ||
    (Boolean(propertyID) && !hasFullPropertyDetails) ||
    (needsUnitQuote && !unitData && !unitDataError);

  const [applicationsList, setApplicationsList] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      trigger("password");
      trigger("confirmPassword");
      // Identify even when the URL has personId. Co-applicant invites always
      // include it. Email prefill stays off that path so a signed-in head of
      // household does not overwrite the invitee's address. This screen is
      // /application/register, so it stays inside the application capture gate.
      if (user?.sub) {
        identifyFullStoryUser(String(user.sub), { email: user.email });
      }
      if (user?.email && !personId) {
        setValue("email", user.email);
      }

      if (user && propertyID) {
        // fetch all active applications for the authenticated user
        getApplications({
          ...(personId ? { personId: personId } : { email: user.email! }),
          propertyId: propertyID,
        }).then((res) => {
          const { data } = res as { data: any };
          if (data && data.length) {
            const activeApplications = data.filter(
              (app: any) => ["pending", "submitted"].indexOf(app.status) >= 0
            );
            setApplicationsList(activeApplications);
          }
        });
      }
    }
  }, [isAuthenticated, trigger, user, setValue, leaseId, propertyID, personId]);

  const [
    triggerFetch,
    { data: leasePersonData, isLoading: isLeasePersonLoading },
  ] = useLazyGetLeasePersonQuery();

  const inviteEmail = leasePersonData?.Email as string | undefined;
  // Localhost-only e2e harness: ?e2eAuthEmail= simulates a logged-in Auth0
  // session without Universal Login (gstack $B cannot reliably type Auth0).
  const e2eAuthEmail =
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)
      ? searchParams.get("e2eAuthEmail")
      : null;
  const effectiveAuthEmail = e2eAuthEmail || user?.email;
  const effectiveIsAuthenticated = Boolean(e2eAuthEmail) || isAuthenticated;
  const inviteAuthMismatch =
    Boolean(personId) &&
    effectiveIsAuthenticated &&
    isInviteAuthMismatch({
      authEmail: effectiveAuthEmail,
      inviteEmail,
    });

  const handleInviteAuthSignOut = () => {
    safeLogout(logout, {
      logoutParams: { returnTo: window.location.href },
    });
  };

  // LIHTC routing: when a co-applicant clicks the invite link and signs in,
  // forward them based on lease.isAffordable + application.role. Standard
  // (non-affordable) applications fall through to the existing flow.
  // Never auto-forward when the Auth0 session is a different person (HoH hijack).
  useEffect(() => {
    if (!isAuthenticated || !applicationId) return;
    if (inviteAuthMismatch) return;
    // If this is an invite and we don't know the invite email yet, wait —
    // otherwise a HoH session could race into the co-applicant editor.
    if (personId && !inviteEmail) return;
    let cancelled = false;
    (async () => {
      const res = (await getApplications({
        id: applicationId,
        propertyId:
          searchParams.get("propertyId") ||
          searchParams.get("propertyID") ||
          "",
      })) as { data?: any[] };
      if (cancelled) return;
      const app = res.data?.find((a: any) => a.id === applicationId);
      const isAffordable = app?.lease?.isAffordable === true;
      const role = app?.role;
      if (!isAffordable) return;
      // A live-in aide is an occupant with a dedicated sub-flow (income/asset
      // screens don't apply); other non-head adults go to the co-applicant flow.
      const isAide = /live.?in.?aide/i.test(role || "");
      const target =
        role && role !== "Head of Household"
          ? isAide
            ? "/application/affordable/live-in-aide"
            : "/application/affordable/co-applicant"
          : "/application/affordable/form";
      const qs = new URLSearchParams({
        applicationId,
        propertyId:
          searchParams.get("propertyId") ||
          searchParams.get("propertyID") ||
          "",
        leaseId: searchParams.get("leaseId") || app?.lease?.id || "",
      }).toString();
      navigate(`${target}?${qs}`, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    applicationId,
    personId,
    inviteEmail,
    inviteAuthMismatch,
  ]);

  const getRent = () => {
    // const specialNoDateRange =
    //   unitData?.special && !unitData?.special?.DateRange;
    // const originalRent = specialNoDateRange
    //   ? (
    //       parseFloat(unitData?.rent ?? 0) +
    //       parseFloat(unitData?.special?.Value ?? 0)
    //     ).toFixed(2)
    //   : null;
    return unitData?.rent;
  };

  // ResMan only publishes a ~15-day pricing window per unit. Deep-links from
  // property websites can land here with a moveInDate outside that window —
  // the API then throws "Move in date ... exceeds the maximum days after
  // availability." We catch that specific case and bounce the user to the
  // availability list so they can pick a date that has a quote. Existing-lease
  // / co-applicant invites must NOT use this recovery — the lease already
  // locked rent + move-in (API returns lease-backed unit data instead).
  const handleSelectNewDate = () => {
    const code = propertyDetails?.abbreviation;
    if (!code) {
      navigate(`/availability`);
      return;
    }
    const params = new URLSearchParams();
    if (moveInDate) {
      const parsed = moment(moveInDate, "MM/DD/YYYY", true).isValid()
        ? moment(moveInDate, "MM/DD/YYYY")
        : moment(moveInDate);
      if (parsed.isValid()) {
        params.set("moveInDate", parsed.format("YYYY-MM-DD"));
      }
    }
    // Availability search filters by unitNumber only — skip unitID (UUID)
    // fallback to avoid a UUID being interpreted as a unit number.
    if (unitNumber) params.set("unitNumber", unitNumber);
    const qs = params.toString();
    navigate(`/availability/${code}${qs ? `?${qs}` : ""}`);
  };

  const appSubmit = handleSubmit(async (data) => {
    if (inviteAuthMismatch) {
      setErrorMessage(
        t("account.registerProspect.inviteAuthMismatchBody", {
          email: inviteEmail,
        }).replace(/<[^>]+>/g, "")
      );
      return;
    }
    FullStory("setProperties", {
      type: "user",
      properties: {
        email: data.email,
      },
    });
    let name = {
      FirstName: data.firstName,
      MiddleName: data.middleName ?? "",
      LastName: data.lastName,
    };
    const selectedTerm = unitData?.term ?? term;
    const req: any = {
      propertyId: propertyID,
      prospect: {
        LeadManagement: {
          Prospects: {
            Prospect: {
              Customers: {
                Customer: {
                  Identification: personId
                    ? {
                        $IDType: "PersonID",
                        $IDValue: personId,
                      }
                    : undefined,
                  Name: name,
                  Email: data.email,
                  $Type: "prospect",
                  Language: data.language ?? "en",
                },
              },
              Events: {
                Event: {
                  Agent: {
                    AgentID: {
                      $IDValue: data.leasingAgent
                        ? data.leasingAgent
                        : "00000000-0000-0000-0000-000000000000",
                    },
                  },
                  FirstContact: "true",
                  $EventDate: moment().format("M/D/YYYY"),
                  $EventType: "prospect",
                  ...(data.prospectSource
                    ? { TransactionSource: data.prospectSource }
                    : {}),
                },
              },
            },
          },
        },
      },
      unit: unitData.number,
      rent: getRent(),
      moveInDate:
        unitData?.moveInDate || moveInDate
          ? new Date(unitData?.moveInDate ?? moveInDate)
          : undefined,
      term: selectedTerm,
      applicationId: applicationId,
      leaseId: leaseId,
      prospectSource:
        unitData.prospectSources?.find(
          (source: any) => source.prospectSourceId === data.prospectSource
        )?.name || "",
      leasingAgent:
        unitData.propertyEmployees?.find(
          (agent: any) => agent.ID === data.leasingAgent
        )?.Name || "",
      ...(data.referrerName ? { referrerName: data.referrerName } : {}),
      specials: unitData?.unitSpecials,
    };
    if (!isAuthenticated) {
      req.password = data.password;
    }
    const res: any = await mutation(req);
    if (res?.error?.data) {
      setErrorMessage(
        res.error?.data?.err ||
          res.error?.data ||
          "There is an error. Please Try again."
      );
    } else {
      if (res.data.isExistingApplication) {
        navigate(`/application/list`);
      } else {
        await webAuthnSignIn(true, res.data.applicationId);
      }
    }
  });

  useEffect(() => {
    if (!isPropertyAndUnitValidated) {
      const propertyIdFromParams =
        searchParams.get("propertyID") ??
        (searchParams.get("propertyId") as string);

      if (!unitNumber && !unitID) {
        setPropertyID(propertyIdFromParams);
        setIsPropertyAndUnitValidated(true);
      } else {
        getValidateUnitDataMutation({
          unitID: unitID ?? unitNumber!,
          propertyID: propertyID ?? propertyIdFromParams,
          moveInDate,
          term,
        }).then((res) => {
          const { data } = res as { data: any };
          if (data.propertyId !== propertyIdFromParams) {
            setPropertyID(data.propertyId);
            searchParams.set("propertyID", data.propertyId);
            setSearchParams(searchParams);
            setIsPropertyAndUnitValidated(true);
          } else {
            setPropertyID(propertyIdFromParams);
            setIsPropertyAndUnitValidated(true);
          }
          console.log("Validation response:", data.propertyId);
        });
      }
    } else {
      if (propertyID) {
        getPropertiesForIDMutation(propertyID).then((res) => {
          const { data } = res as { data: any };
          setPropertyDetails(data);
          if (
            shouldRedirectDirectAffordableToAvailability({
              property: data,
              unitID,
              unitNumber,
              applicationId,
              personId,
              leaseId,
            })
          ) {
            navigate(
              `/availability/${encodeURIComponent(
                data.abbreviation || propertyID || ""
              )}`,
              { replace: true }
            );
            return;
          }
          const missingLeaseTermPath = buildMissingLeaseTermAvailabilityPath({
            property: data,
            unitID,
            unitNumber,
            moveInDate,
            leaseTerm: term,
            applicationId,
            personId,
            leaseId,
          });
          if (missingLeaseTermPath) {
            navigate(missingLeaseTermPath, { replace: true });
            return;
          }
          if (
            data?.affordableHousing &&
            !unitNumber &&
            !unitID &&
            getAffordableUnitAssignmentMode(data) === "holding"
          ) {
            // Fetch HOLD unit data for holding-mode affordable properties
            getUnitDataMutation({
              unitID: "HOLD",
              moveInDate: null,
              term: null,
              propertyID: propertyID!,
              leaseId: null,
            }).then((unitRes) => {
              const { data: holdUnitData } = unitRes as { data: any };
              setUnitData(holdUnitData);
            });
          }
        });
      }
      if (personId) {
        triggerFetch({ id: personId });
      } else if ((unitNumber || unitID) && !leaseId && hasSelectedLeaseTerm) {
        getUnitDataMutation({
          unitID: unitID ?? unitNumber,
          moveInDate: moveInDate,
          term: term,
          propertyID: propertyID!,
          leaseId: leaseId,
        }).then((res) => {
          const { data } = res as { data: any };
          setUnitData(data);
        });
      }
      if (leasePersonData) {
        setValue("firstName", `${leasePersonData.FirstName}`);
        setValue("lastName", `${leasePersonData.LastName}`);
        setValue("email", `${leasePersonData.Email}`);
        if (!unitData) {
          const lease = leasePersonData.leaseLinks.find(
            (l: any) => l.status === "Pending"
          )?.lease;
          if (lease) {
            getUnitDataMutation({
              unitID: lease.Unit,
              moveInDate: lease.MoveInDate!,
              term: lease.term!,
              propertyID: propertyID!,
              leaseId: leaseId,
            }).then((res) => {
              const { data } = res as { data: any };
              setUnitData(data);
              console.log("Fetched unit data based on lease info:", data);
            });
          }
        }
      }
      if (user && !personId) {
        console.log("User info from auth0:", user);
        setValue("email", `${user.email}`);
      }
    }
  }, [
    propertyID,
    leasePersonData,
    personId,
    unitID,
    unitNumber,
    user,
    isPropertyAndUnitValidated,
  ]);

  const propertyHeaderName = propertyDetails.name;

  return (
    <>
      {(isLoadingGetPropertiesForID ||
        isLoadingCreateProspect ||
        isLeasePersonLoading ||
        isLoadingGetUnitData ||
        isRegisterContextLoading) && <ManualLoader />}

      <WizardShell propertyName={propertyHeaderName}>
        <RegisterReviewContainer ref={formContainerRef}>
          {inviteAuthMismatch ? (
            <Box
              component={"div"}
              className={"container_content"}
              sx={{ marginTop: 16, padding: 3 }}
            >
              <Messages
                messageHeading={t(
                  "account.registerProspect.inviteAuthMismatchTitle"
                )}
                messageContent={t(
                  "account.registerProspect.inviteAuthMismatchBody",
                  { email: inviteEmail }
                ).replace(/<\/?strong>/g, "")}
              />
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={handleInviteAuthSignOut}
              >
                {t("account.registerProspect.inviteAuthSignOutButton")}
              </Button>
            </Box>
          ) : !unitReviewed ? (
            <>
              {unitDataError ? (
                shouldShowMoveInOutOfWindowRecovery({
                  leaseId,
                  error: unitDataError,
                }) ? (
                  <Box
                    component={"div"}
                    className={"container_content"}
                    sx={{
                      marginTop: 16,
                      padding: 3,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="h6" sx={{ marginBottom: 1 }}>
                      Move-in date not available
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ marginBottom: 3 }}
                    >
                      {`We don't have pricing for ${
                        moveInDate
                          ? moment(
                              moveInDate,
                              moment(moveInDate, "MM/DD/YYYY", true).isValid()
                                ? "MM/DD/YYYY"
                                : undefined
                            ).format("MMMM D, YYYY")
                          : "this date"
                      }${
                        unitNumber || unitID
                          ? ` on unit ${unitNumber || unitID}`
                          : ""
                      }. Pick a different move-in date to see available units.`}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSelectNewDate}
                    >
                      Select a new date
                    </Button>
                  </Box>
                ) : (
                  <Box
                    component={"div"}
                    className={"container_content"}
                    sx={{ marginTop: 16 }}
                  >
                    There was an error <br />
                    {(unitDataError as any)?.data?.message}{" "}
                  </Box>
                )
              ) : (
                <Box
                  component={"div"}
                  className={"container_content"}
                  sx={{ marginTop: 16 }}
                >
                  <ConfirmUnitInformation
                    options={{
                      data: unitData,
                      property: propertyDetails,
                      query: { pricing, moveInDate, leaseId },
                    }}
                  />

                  <Box
                    sx={{
                      backgroundColor: "#f8f9fa",
                      padding: 3,
                      borderRadius: 2,
                      marginY: 3,
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
                      fontFamily: "'Public Sans', sans-serif",
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        marginBottom: 2,
                        color: "#387C88",
                        fontSize: "18px",
                        fontFamily: "'Public Sans', sans-serif",
                      }}
                    >
                      {t("account.registerProspect.applicationNoticeTitle")}
                    </Typography>
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          marginBottom: 2,
                          lineHeight: 1.6,
                          color: "text.secondary",
                          fontSize: "14px",
                          fontFamily: "'Public Sans', sans-serif",
                          "& ul": {
                            margin: "8px 0 0",
                            paddingLeft: "1.25rem",
                          },
                          "& li": { marginBottom: "4px" },
                        }}
                      >
                        <Trans
                          i18nKey="account.registerProspect.applicationNoticeDescription1"
                          components={{ ul: <ul></ul>, li: <li></li> }}
                        />
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          marginBottom: 2,
                          lineHeight: 1.6,
                          color: "text.secondary",
                          fontSize: "14px",
                          fontFamily: "'Public Sans', sans-serif",
                        }}
                      >
                        <Trans
                          i18nKey="account.registerProspect.applicationNoticeDescription2"
                          components={{ strong: <strong /> }}
                        />
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          marginBottom: 2,
                          lineHeight: 1.6,
                          color: "text.secondary",
                          fontSize: "14px",
                          fontFamily: "'Public Sans', sans-serif",
                        }}
                      >
                        <Trans
                          i18nKey="account.registerProspect.applicationNoticeDescription3"
                          components={{ strong: <strong /> }}
                        />
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.6,
                          color: "text.secondary",
                          fontSize: "14px",
                          fontFamily: "'Public Sans', sans-serif",
                        }}
                      >
                        <Trans
                          i18nKey="account.registerProspect.applicationNoticeDescription4"
                          components={{ strong: <strong /> }}
                        />
                      </Typography>
                    </>
                  </Box>

                  <Button
                    variant="containedLarge"
                    color="primary"
                    type="submit"
                    fullWidth
                    sx={{ overflow: "hidden" }}
                    disabled={isRegisterContextLoading}
                    onClick={() => {
                      setUnitReviewed(true);
                    }}
                  >
                    {t("account.registerProspect.continueButton")}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <></>
          )}
          {!inviteAuthMismatch &&
            unitReviewed &&
            (applicationsList.length ? (
              <Box
                component={"div"}
                className={"container_content"}
                sx={{ marginTop: 16 }}
              >
                <Typography variant="h3" marginBottom={3}>
                  {t(
                    "account.registerProspect.activeApplicationsTitle",
                    "Your Active Applications"
                  )}
                </Typography>
                <Typography
                  variant="body1"
                  marginBottom={3}
                  color="text.secondary"
                >
                  {t(
                    "account.registerProspect.activeApplicationsDescription",
                    "You have active applications for the following units:"
                  )}
                </Typography>

                {applicationsList.map((app, index) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: "#f9f9f9",
                      padding: 3,
                      borderRadius: 2,
                      marginBottom: 2,
                      border: "1px solid #e0e0e0",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          color: theme.palette.primary.main,
                        }}
                      >
                        {propertyDetails.name}
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor:
                            app.status === "pending" ? "#fff3cd" : "#d1ecf1",
                          color:
                            app.status === "pending" ? "#856404" : "#0c5460",
                          padding: "4px 12px",
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                        }}
                      >
                        {app.status}
                      </Box>
                    </Box>

                    <Box sx={{ marginBottom: 2 }}>
                      {app.unit && (
                        <Typography variant="body1" sx={{ marginBottom: 0.5 }}>
                          <strong>Unit:</strong> {app.unit}
                        </Typography>
                      )}
                      {app.moveInDate && (
                        <Typography variant="body1" sx={{ marginBottom: 0.5 }}>
                          <strong>Move-in Date:</strong>{" "}
                          {moment(app.moveInDate).format("MMM DD, YYYY")}
                        </Typography>
                      )}
                    </Box>

                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      disabled={isRegisterContextLoading}
                      onClick={async () => {
                        if (app.status === "pending") {
                          const routePropertyDetails =
                            app.property ??
                            (await resolvePropertyDetailsForRouting());
                          const formRoute = isAffordableApplicationFlow(
                            { number: app.unit },
                            routePropertyDetails
                          )
                            ? "/application/affordable/form"
                            : "/application/form";
                          const routePropertyId =
                            app.property?.id || propertyID || "";
                          navigate(
                            `${formRoute}?applicationId=${
                              app.id
                            }&propertyId=${routePropertyId}&leaseId=${
                              app.lease?.id || ""
                            }`
                          );
                        } else if (app.status === "submitted") {
                          navigate(
                            `/application/payment?applicationId=${app.id}${
                              app.lease?.id ? `&leaseid=${app.lease.id}` : ""
                            }`
                          );
                        }
                      }}
                      sx={{ marginTop: 1 }}
                    >
                      {app.status === "pending"
                        ? t(
                            "account.registerProspect.viewApplicationButton",
                            "Continue Application"
                          )
                        : t(
                            "account.registerProspect.viewPaymentButton",
                            "Pay Application Fee"
                          )}
                    </Button>
                  </Box>
                ))}

                {/* <Box sx={{ marginTop: 4, textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    marginBottom={2}
                  >
                    {t(
                      "account.registerProspect.newApplicationPrompt",
                      "Want to start a new application for this unit?"
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setApplicationsList([])}
                    sx={{ marginRight: 2 }}
                  >
                    {t(
                      "account.registerProspect.startNewApplicationButton",
                      "Start New Application"
                    )}
                  </Button>
                </Box> */}
              </Box>
            ) : (
              <FormWrapper onSubmit={appSubmit} className="no_fullscreen">
                <Box
                  component={"div"}
                  className={"container_content"}
                  sx={{
                    [`@media(min-width: ${theme.breakpoints.values.sm}px)`]: {
                      minHeight: 400 + commonSpacing.desktopPaddingB * 4,
                    },
                  }}
                >
                  <Box height={"60px"}></Box>
                  {isAuthenticated ? (
                    <Box marginTop={2} marginBottom={2}>
                      <Typography variant="h3" marginBottom={2}>
                        {t("account.registerProspect.provideInfoTitle")}
                      </Typography>
                    </Box>
                  ) : (
                    <Box marginTop={2} marginBottom={2}>
                      <Typography variant="h3" marginBottom={2}>
                        {t("account.registerProspect.createAccountTitle")}
                      </Typography>
                      <Typography fontSize={12} fontWeight={"bold"}>
                        {t("account.registerProspect.alreadyRegistered")}{" "}
                        <Link
                          onClick={() => webAuthnSignIn()}
                          style={{ cursor: "pointer" }}
                        >
                          {t("account.registerProspect.clickToLogin")}
                        </Link>
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Controller
                      control={control}
                      name="firstName"
                      render={({ field: { onChange, value } }) => (
                        <FormInput
                          label={t("account.registerProspect.firstNameLabel")}
                          id={"firstName"}
                          isErrorMessageVisible={true}
                          {...register("firstName")}
                          error={errors?.firstName?.message}
                          onChange={(event: any) => {
                            updateBindingsChange(event, "firstName", setValue);
                          }}
                          value={value}
                          inputFocus={(e: any) => {
                            trigger("firstName");
                          }}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="lastName"
                      render={({ field: { onChange, value } }) => (
                        <FormInput
                          label={t("account.registerProspect.lastNameLabel")}
                          id={"lastName"}
                          isErrorMessageVisible={true}
                          {...register("lastName")}
                          error={errors?.lastName?.message}
                          onChange={(event: any) => {
                            updateBindingsChange(event, "lastName", setValue);
                          }}
                          value={value}
                          inputFocus={(e: any) => {
                            trigger("lastName");
                          }}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="email"
                      render={({ field: { onChange, value } }) => (
                        <FormInput
                          label={t("account.registerProspect.emailLabel")}
                          id={"email"}
                          {...register("email")}
                          error={errors?.email?.message}
                          onChange={(event: any) => {
                            updateBindingsChange(
                              {
                                ...event,
                                target: {
                                  ...event.target,
                                  value: event.target.value.toLowerCase(),
                                },
                              },
                              "email",
                              setValue
                            );
                          }}
                          inputMode="email"
                          value={value}
                          inputFocus={(e: any) => {
                            trigger("email");
                          }}
                          readonly={isAuthenticated || !!personId}
                        />
                      )}
                    />

                    {!isAuthenticated && (
                      <>
                        <Controller
                          control={control}
                          name="password"
                          render={({ field: { onChange, value } }) => (
                            <FormInput
                              label={t(
                                "account.registerProspect.passwordLabel"
                              )}
                              id={"password"}
                              {...register("password")}
                              error={errors?.password?.message}
                              onChange={(event: any) => {
                                updateBindingsChange(
                                  event,
                                  "password",
                                  setValue
                                );
                              }}
                              value={value}
                              inputFocus={(e: any) => {
                                trigger("password");
                              }}
                              type={showPassword ? "text" : "password"}
                              endAdornment={
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() =>
                                      setShowPassword(
                                        (previousValue) => !previousValue
                                      )
                                    }
                                    onMouseDown={(event) =>
                                      event.preventDefault()
                                    }
                                    edge="end"
                                    type="button"
                                    size="small"
                                    sx={{ marginRight: 0.5 }}
                                  >
                                    {showPassword ? (
                                      <VisibilityOffOutlinedIcon
                                        sx={{ color: "gray.300", fontSize: 16 }}
                                      />
                                    ) : (
                                      <RemoveRedEyeOutlinedIcon
                                        sx={{ color: "gray.300", fontSize: 16 }}
                                      />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              }
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="confirmPassword"
                          render={({ field: { onChange, value } }) => (
                            <FormInput
                              label={t(
                                "account.registerProspect.confirmPasswordLabel"
                              )}
                              id={"confirmPassword"}
                              {...register("confirmPassword")}
                              error={errors?.confirmPassword?.message}
                              onChange={(event: any) => {
                                updateBindingsChange(
                                  event,
                                  "confirmPassword",
                                  setValue
                                );
                              }}
                              value={value}
                              inputFocus={(e: any) => {
                                trigger("confirmPassword");
                              }}
                              type={showConfirmPassword ? "text" : "password"}
                              endAdornment={
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="toggle confirm password visibility"
                                    onClick={() =>
                                      setShowConfirmPassword(
                                        (previousValue) => !previousValue
                                      )
                                    }
                                    onMouseDown={(event) =>
                                      event.preventDefault()
                                    }
                                    edge="end"
                                    type="button"
                                    size="small"
                                    sx={{ marginRight: 0.5 }}
                                  >
                                    {showConfirmPassword ? (
                                      <VisibilityOffOutlinedIcon
                                        sx={{ color: "gray.300", fontSize: 16 }}
                                      />
                                    ) : (
                                      <RemoveRedEyeOutlinedIcon
                                        sx={{ color: "gray.300", fontSize: 16 }}
                                      />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              }
                            />
                          )}
                        />
                      </>
                    )}

                    <Controller
                      control={control}
                      name="language"
                      render={({ field: { onChange, value } }) => (
                        <FormControl
                          fullWidth
                          error={!!errors?.language?.message}
                          sx={{ mb: 3 }}
                        >
                          <InputLabel id="language-label">
                            {t("account.registerProspect.languageLabel")}
                          </InputLabel>
                          <Select
                            labelId="language-label"
                            id="language"
                            value={value || ""}
                            label={t("account.registerProspect.languageLabel")}
                            onChange={(event: any) => {
                              const selectedValue = event.target.value;
                              onChange(selectedValue);
                              i18n.changeLanguage(selectedValue);
                            }}
                            onBlur={() => trigger("language")}
                          >
                            <MenuItem value="en">English</MenuItem>
                            <MenuItem value="es">Spanish</MenuItem>
                          </Select>
                          {errors?.language?.message && (
                            <Box
                              sx={{
                                color: "error.main",
                                fontSize: "0.75rem",
                                mt: 0.5,
                              }}
                            >
                              {errors.language.message}
                            </Box>
                          )}
                        </FormControl>
                      )}
                    />

                    <Controller
                      control={control}
                      name="prospectSource"
                      render={({ field: { onChange, value } }) => (
                        <FormControl
                          fullWidth
                          error={!!errors?.prospectSource?.message}
                          sx={{ mb: 3 }}
                        >
                          <InputLabel id="prospect-source-label">
                            {t("account.registerProspect.prospectSourceLabel")}
                          </InputLabel>
                          <Select
                            labelId="prospect-source-label"
                            id="prospectSource"
                            value={value || ""}
                            label={t(
                              "account.registerProspect.prospectSourceLabel"
                            )}
                            onChange={(event: any) => {
                              const selectedValue = event.target.value;
                              onChange(selectedValue);
                              // Find the selected prospect source name
                              const sourceName =
                                unitData?.prospectSources?.find(
                                  (source: any) =>
                                    source.prospectSourceId === selectedValue
                                )?.name || "";
                              setSelectedProspectSource(sourceName);
                            }}
                            onBlur={() => trigger("prospectSource")}
                          >
                            <MenuItem value="">
                              <em>
                                {t("account.registerProspect.noLeasingAgent")}
                              </em>
                            </MenuItem>
                            {unitData?.prospectSources?.map((source: any) => (
                              <MenuItem
                                key={source.prospectSourceId}
                                value={source.prospectSourceId}
                              >
                                {source.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors?.prospectSource?.message && (
                            <Box
                              sx={{
                                color: "error.main",
                                fontSize: "0.75rem",
                                mt: 0.5,
                              }}
                            >
                              {errors.prospectSource.message}
                            </Box>
                          )}
                        </FormControl>
                      )}
                    />

                    {selectedProspectSource?.toLowerCase().includes("friend") ||
                    selectedProspectSource?.toLowerCase().includes("family") ||
                    selectedProspectSource
                      ?.toLowerCase()
                      .includes("referral") ||
                    selectedProspectSource
                      ?.toLowerCase()
                      .includes("resident") ? (
                      <Controller
                        control={control}
                        name="referrerName"
                        render={({ field: { onChange, value } }) => (
                          <FormInput
                            label={
                              t("account.registerProspect.referrerNameLabel") ||
                              "Name of Friend or Family Member"
                            }
                            id={"referrerName"}
                            isErrorMessageVisible={true}
                            {...register("referrerName")}
                            error={errors?.referrerName?.message}
                            onChange={(event: any) => {
                              updateBindingsChange(
                                event,
                                "referrerName",
                                setValue
                              );
                            }}
                            value={value}
                            inputFocus={(e: any) => {
                              trigger("referrerName");
                            }}
                          />
                        )}
                      />
                    ) : null}

                    <Controller
                      control={control}
                      name="leasingAgent"
                      render={({ field: { onChange, value } }) => (
                        <FormControl
                          fullWidth
                          error={!!errors?.leasingAgent?.message}
                          sx={{ mb: 3 }}
                        >
                          <InputLabel id="leasing-agent-label">
                            {t("account.registerProspect.leasingAgentLabel")}
                          </InputLabel>
                          <Select
                            labelId="leasing-agent-label"
                            id="leasingAgent"
                            value={value || ""}
                            label={t(
                              "account.registerProspect.leasingAgentLabel"
                            )}
                            onChange={(event: any) => {
                              onChange(event.target.value);
                            }}
                            onBlur={() => trigger("leasingAgent")}
                          >
                            <MenuItem value="">
                              <em>
                                {t("account.registerProspect.noLeasingAgent")}
                              </em>
                            </MenuItem>
                            {unitData?.propertyEmployees?.map(
                              (employee: any) => (
                                <MenuItem key={employee.ID} value={employee.ID}>
                                  {employee.Name}
                                </MenuItem>
                              )
                            )}
                          </Select>
                          {errors?.leasingAgent?.message && (
                            <Box
                              sx={{
                                color: "error.main",
                                fontSize: "0.75rem",
                                mt: 0.5,
                              }}
                            >
                              {errors.leasingAgent.message}
                            </Box>
                          )}
                        </FormControl>
                      )}
                    />

                    <FormSwitch
                      label={t("account.registerProspect.consentCommsLabel")}
                      id={"confirmComms"}
                      checked={isconfirmComms}
                      onChange={(e: any) => {
                        setconfirmComms(e.target.checked);
                      }}
                    />
                    <FormSwitch
                      label={t("account.registerProspect.privacyPolicyLabel")}
                      labelLink={{
                        label: t("account.registerProspect.privacyPolicyLink"),
                        url: "https://tenantport.s3.amazonaws.com/documents/PrivacyPolicy-TenantPort.pdf",
                      }}
                      id={"confirmPrivacy"}
                      onChange={(e: any) => {
                        setconfirmPrivacy(e.target.checked);
                      }}
                      checked={isconfirmPrivacy}
                    />
                    <FormSwitch
                      label={t("account.registerProspect.rentalCriteriaLabel")}
                      labelLink={{
                        label: t("account.registerProspect.rentalCriteriaLink"),
                        url: `https://resprop.myresman.com/Portal/Applicants/RentalCriteria?propertyID=${propertyID}&accountID=1473`,
                      }}
                      id={"confirmCriteria"}
                      onChange={(e: any) => {
                        setconfirmCriteria(e.target.checked);
                      }}
                      checked={isconfirmCriteria}
                    />
                  </Box>
                  {errorMessage !== "" && (
                    <Messages
                      messageHeading={t(
                        "account.registerProspect.registrationErrorHeading"
                      )}
                      messageContent={errorMessage}
                      closeEvent={() => setErrorMessage("")}
                    />
                  )}
                </Box>
                <Button
                  variant="containedLarge"
                  color="primary"
                  type="submit"
                  fullWidth
                  sx={{ overflow: "hidden" }}
                  disabled={
                    !(
                      isconfirmPrivacy &&
                      isconfirmCriteria &&
                      isconfirmComms
                    ) ||
                    isLoadingCreateProspect ||
                    isRegisterContextLoading
                  }
                >
                  {t("account.registerProspect.createAccountButton")}
                </Button>
              </FormWrapper>
            ))}
        </RegisterReviewContainer>

        <Box>
          <Footer />
        </Box>
      </WizardShell>
    </>
  );
};
